/**
 * Email service functions
 * 
 * This module provides functions for sending emails using EmailJS
 */

import emailjs from 'emailjs-com';

// Initialize EmailJS with your user ID
// This should be called once when your app initializes
export const initEmailJS = () => {
  emailjs.init(process.env.REACT_APP_EMAILJS_USER_ID);
};

/**
 * Send a confirmation email to a user after booking an appointment
 * 
 * @param {Object} appointmentData - The appointment data
 * @param {string} email - The recipient's email address
 * @returns {Promise} - Promise that resolves when the email is sent
 */
export const sendAppointmentConfirmationEmail = async (appointmentData, email) => {
  try {
    // Format date for display
    const formatDate = (dateString) => {
      if (!dateString) return '';
      
      const [year, month, day] = dateString.split('-').map(Number);
      const dateObj = new Date(year, month - 1, day);
      
      return dateObj.toLocaleDateString('en-GB', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    // Format time for display
    const formatTime = (timestamp) => {
      if (!timestamp) return '';
      
      let date;
      
      // Handle different timestamp formats
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Firebase Timestamp object with toDate method
        date = timestamp.toDate();
      } else if (timestamp.seconds !== undefined && timestamp.nanoseconds !== undefined) {
        // Plain object with seconds and nanoseconds (serialized Firestore timestamp)
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        // JavaScript Date object
        date = timestamp;
      } else {
        console.error('Invalid timestamp:', timestamp);
        return 'Invalid time';
      }
      
      return date.toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    };

    // Prepare template parameters
    const templateParams = {
      to_email: email,
      to_name: appointmentData.userInfo.name,
      appointment_date: formatDate(appointmentData.date),
      appointment_time: `${formatTime(appointmentData.slot.startTime)} - ${formatTime(appointmentData.slot.endTime)}`,
      practice_name: appointmentData.practiceName,
      practice_address: appointmentData.practiceAddress || 'Address information not available',
      practice_phone: appointmentData.practicePhone || 'Phone information not available',
      patient_name: appointmentData.userInfo.name,
      patient_phone: appointmentData.userInfo.phone,
      patient_postcode: appointmentData.userInfo.postcode,
      patient_issue: appointmentData.userInfo.issue || 'No specific issue mentioned',
      appointment_id: appointmentData.id || 'ID not available'
    };

    // Send the email using EmailJS
    const response = await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
      templateParams
    );

    console.log('Email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Error sending confirmation email:', error);
    throw error;
  }
};
